(function (root, factory) {
  'use strict';
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.HarrodQuizFilters = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  const isFilter = filter => filter && /^filter\.p\.m\.custom\.[a-z_]+$/.test(filter.param)
    && /^gid:\/\/shopify\/Metaobject\/\d+$/.test(String(filter.value));
  const valuesOf = (params, key) => params.getAll(key).flatMap(value => value.split(',')).filter(Boolean);
  const sameFilter = (a, b) => a.param === b.param && a.value === b.value;
  const uniqueFilters = filters => filters.filter((filter, index) => filters.findIndex(item => sameFilter(item, filter)) === index);
  const questionsOf = style => style && Array.isArray(style.questions) ? style.questions : [];
  const treeFields = ['height', 'spread'];
  const activeTreeFields = ['height', 'spread', 'crown', 'roots', 'lifespan', 'biodiversity', 'carbon'];
  const isTreeFilter = filter => filter && treeFields.includes(filter.field) && /^[a-z0-9][a-z0-9-]*$/.test(String(filter.value));

  function filtersForChoice(choice) {
    return choice && Array.isArray(choice.nativeFilters) ? choice.nativeFilters.filter(isFilter) : [];
  }

  function treeFiltersForChoice(choice) {
    return choice && Array.isArray(choice.treeFilters) ? choice.treeFilters.filter(isTreeFilter) : [];
  }

  function countState(state, href) {
    if (state.count === 0) return 0;
    const url = new URL(href, 'https://harrod-outdoors.myshopify.com');
    const selected = activeTreeFields.map(field => [field, valuesOf(url.searchParams, 'ho_tree_' + field)]).filter(([, values]) => values.length);
    if (!selected.length) return state.count;
    if (state.treeComplete !== true || !Array.isArray(state.treeProducts)) return null;
    return state.treeProducts.filter(product => product.eligible !== false && selected.every(([field, values]) =>
      values.some(value => (product.fields?.[field] || []).includes(value)))).length;
  }

  function ownedFilters(style) {
    const explicit = style && Array.isArray(style.nativeOwnedFilters) ? style.nativeOwnedFilters : [];
    return uniqueFilters(explicit.concat(questionsOf(style).flatMap(question =>
      (question.choices || []).flatMap(filtersForChoice))).filter(isFilter));
  }

  function normalizeAnswers(style, supplied) {
    const answers = {};
    questionsOf(style).forEach(question => {
      const choices = question.choices || [];
      const choice = choices.find(item => supplied && item.value === supplied[question.key]);
      const fallback = choices.find(item => item.value === 'any');
      answers[question.key] = choice ? choice.value : fallback ? fallback.value : null;
    });
    return answers;
  }

  function selectedFilters(style, supplied) {
    const answers = normalizeAnswers(style, supplied);
    const filters = [];
    const treeFilters = [];
    const unsupported = [];
    questionsOf(style).forEach(question => {
      const choice = (question.choices || []).find(item => item.value === answers[question.key]);
      if (!choice || choice.value === 'any') return;
      const native = filtersForChoice(choice);
      const trees = treeFiltersForChoice(choice);
      if (!native.length && !trees.length && !(style.search && typeof choice.query === 'string')) unsupported.push({ question: question.key, choice: choice.value, label: choice.label || choice.value });
      else {
        filters.push(...native.map(filter => ({...filter, question:question.key})));
        treeFilters.push(...trees.map(filter => ({...filter, question:question.key})));
      }
    });
    return { answers, filters: uniqueFilters(filters), treeFilters, unsupported };
  }

  function buildFilterUrl(currentHref, style, supplied, options) {
    options = options || {};
    const url = new URL(currentHref, options.origin || 'https://harrod-outdoors.myshopify.com');
    const selected = selectedFilters(style, supplied);
    if (style.search) {
      const queries = questionsOf(style).map(question => (question.choices || []).find(choice => choice.value === selected.answers[question.key])?.query).filter(Boolean);
      if (queries.length) {
        url.searchParams.set('q', queries.join(' '));
        url.searchParams.set('type', 'product');
        url.searchParams.set('ho_quiz_search', style.key);
      } else {
        url.pathname = url.pathname.replace(/\/search\/?$/, '/collections/all');
        ['q','type','ho_quiz_search'].forEach(key => url.searchParams.delete(key));
      }
    }
    const owned = ownedFilters(style);
    const answeredKeys = new Set(selected.filters.map(filter => filter.param));
    const keys = [...new Set(owned.map(filter => filter.param))];
    keys.forEach(key => {
      const ownedValues = new Set(owned.filter(filter => filter.param === key).map(filter => String(filter.value)));
      const preserved = answeredKeys.has(key) ? [] : [...new Set(valuesOf(url.searchParams, key).filter(value => !ownedValues.has(value)))];
      url.searchParams.delete(key);
      preserved.forEach(value => url.searchParams.append(key, value));
    });
    selected.filters.forEach(filter => {
      if (!valuesOf(url.searchParams, filter.param).includes(filter.value)) url.searchParams.append(filter.param, filter.value);
    });
    treeFields.forEach(field => {
      const ownedValues = new Set(questionsOf(style).flatMap(question => (question.choices || []).flatMap(treeFiltersForChoice)).filter(filter => filter.field === field).map(filter => filter.value));
      if (!ownedValues.size) return;
      const chosen = selected.treeFilters.filter(filter => filter.field === field);
      const preserved = chosen.length ? [] : valuesOf(url.searchParams, 'ho_tree_' + field).filter(value => !ownedValues.has(value));
      url.searchParams.delete('ho_tree_' + field);
      [...new Set([...preserved, ...chosen.map(filter => filter.value)])].forEach(value => url.searchParams.append('ho_tree_' + field, value));
    });
    url.searchParams.delete('page');
    url.searchParams.delete('ho_tree_page');
    if (options.hash) url.hash = options.hash;
    const conflicts = [];
    const groups = new Map();
    selected.filters.forEach(filter => {
      if (!groups.has(filter.param)) groups.set(filter.param, []);
      groups.get(filter.param).push(filter);
    });
    groups.forEach((filters, param) => {
      const questionKeys = [...new Set(filters.map(filter => filter.question).filter(Boolean))];
      if (questionKeys.length > 1 && options.operators?.[param] !== 'AND') {
        conflicts.push({ param, operator: 'OR', questions: questionKeys });
      }
    });
    treeFields.forEach(field => {
      const questionKeys = [...new Set(selected.treeFilters.filter(filter => filter.field === field).map(filter => filter.question))];
      if (questionKeys.length > 1) conflicts.push({param:'ho_tree_' + field, operator:'OR', questions:questionKeys});
    });
    return { url: url.href, answers: selected.answers, filters: selected.filters, treeFilters:selected.treeFilters, unsupported: selected.unsupported, conflicts };
  }

  return { buildFilterUrl, selectedFilters, normalizeAnswers, ownedFilters, filtersForChoice, treeFiltersForChoice, countState };
});



